import { z } from 'zod';
import { type FolderColor, folderColorSchema, type Platform, platformSchema } from './common';

export const PAGE_KINDS = ['cliente', 'mio'] as const;
export type PageKind = (typeof PAGE_KINDS)[number];
export const pageKindSchema = z.enum(PAGE_KINDS);

export const CHANNEL_STATUSES = ['da_collegare', 'collegato', 'errore'] as const;
export type ChannelStatus = (typeof CHANNEL_STATUSES)[number];

const text = (max: number) => z.string().trim().max(max);

export const pageCreateSchema = z.object({
  name: z.string().trim().min(1, 'Scrivi un nome').max(80, 'Il nome è troppo lungo'),
  color: folderColorSchema,
  kind: pageKindSchema.optional(),
  description: text(2000).optional(),
  audience: text(1000).optional(),
  tone: text(1000).optional(),
  website: text(300).optional(),
  phone: text(50).optional(),
  address: text(300).optional(),
  notes: text(20000).optional(),
});
export type PageCreateInput = z.infer<typeof pageCreateSchema>;

export const pageUpdateSchema = pageCreateSchema
  .extend({ archived: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Niente da salvare' });
export type PageUpdateInput = z.infer<typeof pageUpdateSchema>;

export const pageReorderSchema = z.object({ ids: z.array(z.string().min(1)).min(1).max(500) });

export const channelCreateSchema = z.object({
  platform: platformSchema,
  label: text(80).optional(),
  handle: text(120).optional(),
  url: text(500).optional(),
  externalId: text(200).optional(),
  postsPerWeek: z.number().int().min(0).max(50).nullable().optional(),
  config: z.record(z.string().max(40), z.string().max(300)).optional(),
});
export type ChannelCreateInput = z.infer<typeof channelCreateSchema>;

export const channelUpdateSchema = channelCreateSchema
  .omit({ platform: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Niente da salvare' });
export type ChannelUpdateInput = z.infer<typeof channelUpdateSchema>;

export interface Channel {
  id: string;
  pageId: string;
  platform: Platform;
  label: string;
  handle: string;
  url: string;
  externalId: string;
  postsPerWeek: number | null;
  status: ChannelStatus;
  statusDetail: string;
  /** Impostazioni extra, per esempio { gsc: 'sc-domain:x.it', ga4: 'properties/123' }. */
  config: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface Page {
  id: string;
  slug: string;
  name: string;
  kind: PageKind;
  color: FolderColor;
  logoUrl: string | null;
  description: string;
  audience: string;
  tone: string;
  website: string;
  phone: string;
  address: string;
  notes: string;
  archived: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  channels: Channel[];
}
